<template>
  <div class="pui-wrapper bea-demands">
    <!-- Global spinner -->
    <pui-common-spinner
      v-if="globalLoader.messages.length"
      :message="globalLoader.messages[0].message"
      fullscreen
      hideBackgroundCircle
    />

    <!-- Delete or Archiving dialog -->
    <pui-dialog ref="confirmDeletionDialog" :showCloseButton="false" transition="slide-up">
      <h3 class="message">
        <span class="mdi mdi-cancel" />{{ $t('poster.demands.delete.message') }}
      </h3>
      <pui-button-list class="comfirm-dialog__actions">
        <pui-button @click="cancelDeletion" variant="primary" outline>
          {{ $t('poster.demands.delete.cancel') }}
        </pui-button>
        <pui-button @click="confirmDeletion" variant="primary">
          {{ $t('poster.demands.delete.confirm') }}
        </pui-button>
      </pui-button-list>
    </pui-dialog>
    <pui-dialog ref="confirmArchivingDialog" :showCloseButton="false" transition="slide-up">
      <h3 class="message">
        <span class="mdi mdi-trash-can" />{{ $t('poster.demands.archive.message') }}
      </h3>
      <pui-button-list class="comfirm-dialog__actions">
        <pui-button @click="cancelArchiving" variant="primary" outline>
          {{ $t('poster.demands.archive.cancel') }}
        </pui-button>
        <pui-button @click="confirmArchiving" variant="primary">
          {{ $t('poster.demands.archive.confirm') }}
        </pui-button>
      </pui-button-list>
    </pui-dialog>

    <!-- Confirmation dialog -->
    <pui-send-poster-summary-dialog
      ref="sendPosterSummaryDialog"
      @closed="onSendPosterDialogClosed"
      @creationConfirmed="onSendPosterDialogConfirmation"
      :currentAction="currentAction"
      :generatePreviews="generatePreviewData"
      :showGenericError="fontsError"
    >
      <template #genericError>
        <pui-flex
          v-if="fontsError"
          class="preview-fonts-error"
          flex="1"
          direction="column"
          alignItems="center"
        >
          <i class="mdi mdi-alert error-icon" />
          <i18n path="poster.demands.editing.error.fonts.primary"></i18n>
          <i18n path="poster.demands.editing.error.fonts.secondary">
            <template #clickToReload>
              <span @click="reloadFromFontsError" class="reload-button">{{
                $t('poster.demands.editing.error.fonts.click_to_reload')
              }}</span>
            </template>
          </i18n>
        </pui-flex>
      </template>

      <template #posterPreview="{ preview, showAllPrintables }">
        <pui-svg-signage-preview
          v-if="preview.mimeType === PosterMimeTypes.Poster"
          :showBackground="false"
          :template="preview"
          :bindings="preview.bindings"
          :types="preview.types"
          :showAllPrintables="showAllPrintables"
        />
        <poster-gallery-thumbnail v-else :posterId="preview.itemId" />
      </template>

      <template #previewInformation="{ preview }">
        <pui-attribute-detail-panel
          v-for="printDetail in printDetailsDefinition"
          :key="printDetail.alias"
          :value="
            printDetail.attribute != null ? preview.bindings[printDetail.attribute.alias] : null
          "
          :detail="printDetail"
          :getRenderer="getRenderer"
        />
        <p class="pui-attribute-detail-panel">
          <span class="attribute-detail-label">{{
            $t('poster.demands.send_summary_dialog.item_info.type')
          }}</span>
          <span class="attribute-detail-value">{{ preview.type | piivoTranslate }}</span>
        </p>
        <p class="pui-attribute-detail-panel">
          <span class="attribute-detail-label">{{
            $t('poster.demands.send_summary_dialog.item_info.copy_count.label')
          }}</span>
          <span class="attribute-detail-value">
            {{
              $t('poster.demands.send_summary_dialog.item_info.copy_count.count', {
                count: preview.copies,
                format: piivoTranslate(preview.format),
              })
            }}</span
          >
        </p>
      </template>
    </pui-send-poster-summary-dialog>

    <!-- Send limited selection dialog -->
    <poster-send-limited-count-confirmation-dialog
      ref="sendLimitedCountConfirmationDialog"
      :nbSignageItems="selectedSignages.length"
      :posterLimit="posterLimit"
      page="demands"
    ></poster-send-limited-count-confirmation-dialog>

    <!-- Signages listing -->
    <div class="signages-listing">
      <div class="left-panel">
        <pui-common-spinner
          v-show="isDemandsLoading"
          :message="currentDemandsMessage"
          position="fixed"
        />
        <div class="left-panel-top">
          <div class="" style="display: flex;">
            <pui-button-list class="top-buttongroup-view">
              <pui-button
                v-track:click="Tracking.LOGS_MODE_LIST"
                :title="$t('poster.demands.gallery.mode.list.title')"
                :class="{ active: viewMode === ViewModes.LIST }"
                :flat="true"
                @click="switchViewMode(ViewModes.LIST)"
                picto="mdi-menu"
                variant="primary"
              />
              <pui-button
                v-track:click="Tracking.LOGS_MODE_GALLERY"
                :title="$t('poster.demands.gallery.mode.grid.title')"
                :class="{ active: viewMode === ViewModes.GALLERY }"
                :flat="true"
                @click="switchViewMode(ViewModes.GALLERY)"
                picto="mdi-view-grid"
                variant="primary"
              />
            </pui-button-list>
            <pui-button
              :flat="true"
              @click="toggleSelectAllDemands()"
              class="btn-selectall"
              variant="primary"
              style="width:176px"
            >
              {{
                noSelectedElements()
                  ? $t('poster.demands.gallery.select_all')
                  : $t('poster.demands.gallery.deselect_all')
              }}
            </pui-button>
          </div>

          <pui-pagination
            v-if="signageRequests && signageRequests.length > 0"
            :total="pagination.totalNumberOfItems"
            :pageSize="pagination.pageSize"
            :value="pagination.pageIndex"
            @change="onPageIndexChanged"
            pageMode="determinate"
            class="panel-search-result__footer-pagination"
          />
          <pui-button
            v-track:click="Tracking.LOGS_TOGGLE_FILTERS_PANEL"
            :class="{
              'btn-filtre': true,
              filterOpened: $refs.filtersPanel != null && $refs.filtersPanel.opened,
            }"
            :flat="false"
            :outline="true"
            @click="toggleFiltersPanel"
            variant="primary"
          >
            {{
              $t('poster.demands.gallery.filters.button', {
                count: nbAppliedFilters,
              })
            }}
          </pui-button>
        </div>

        <!-- Grid demands list -->
        <div v-show="viewMode === ViewModes.LIST" class="demands-list">
          <pui-table
            ref="demandsTable"
            v-show="signageRequests && signageRequests.length > 0"
            :columns="columns"
            :allowSelection="true"
            :showCheckBoxSelectAll="true"
            :items="signageRequests"
            :columnSorts="currentSorts"
            @columnSort="onColumnSort"
            @selectionChanged="onSelectionchanged"
            :tableEditors="PosterTableEditors"
            :tableRenderers="PosterTableRenderers"
            :getItemId="getPosterId"
          />
          <h2
            v-show="!signageRequests || (signageRequests.length === 0 && !isDemandsLoading)"
            class="demands-nodata"
          >
            <span class="mdi mdi-table-large" />{{ $t('poster.demands.gallery.no_data') }}
          </h2>
        </div>

        <!-- Gallery demands list -->
        <div v-show="viewMode === ViewModes.GALLERY" class="demands-gallery scroll-body">
          <ul class="gallery-container">
            <li
              v-for="(signageRequest, requestIndex) in signageRequests"
              :key="requestIndex"
              :class="{
                'gallery-element': true,
                selected: $refs.demandsTable.isSelected(signageRequest),
              }"
            >
              <div @click="$refs.demandsTable.toggleItems([signageRequest])" class="select-button">
                <span class="mdi mdi-check-bold" />
              </div>
              <div
                @click.stop="$refs.demandsTable.onRowClick(signageRequest, $event)"
                class="gallery-element-hover"
              >
                <div class="gallery-buttonbar">
                  <pui-button-list class="">
                    <pui-button
                      v-permission="Permissions.LOGS_EDIT"
                      v-track:click="Tracking.LOGS_EDIT_SIGNAGE"
                      :outline="false"
                      :title="$t('poster.demands.gallery.item.open')"
                      @click.stop="openEditDialog(signageRequest)"
                      picto="mdi-pencil-box"
                      variant="primary"
                    />
                    <pui-button
                      v-permission="Permissions.LOGS_ARCHIVE"
                      :outline="false"
                      :title="$t('poster.demands.gallery.item.archive')"
                      @click.stop="archiveSignage(signageRequest)"
                      picto="mdi-trash-can"
                      variant="primary"
                    />
                    <pui-button
                      v-permission="Permissions.LOGS_DELETE"
                      :outline="false"
                      :title="$t('poster.demands.gallery.item.delete')"
                      @click.stop="deleteSignage(signageRequest)"
                      picto="mdi-cancel"
                      variant="primary"
                    />
                  </pui-button-list>
                </div>
              </div>
              <poster-gallery-thumbnail
                :posterId="signageRequest.itemId"
                class="responsive-image"
              />
              <p class="picto-info">
                <component
                  :is="taintedRenderer"
                  :value="signageRequest.signages_logs_table_tainted"
                />
                <component
                  :is="commentRenderer"
                  :value="signageRequest.signages_logs_table_comment"
                />
              </p>
              <pui-flex alignItems="center" justifyContent="center">
                <span
                  :style="{
                    backgroundColor: getPosterStatusColor(
                      signageRequest.signages_logs_table_status
                    ),
                  }"
                  class="poster-status-color"
                />
                {{ signageRequest.signages_logs_table_type | piivoTranslate }}
                {{ signageRequest.signages_logs_table_format | piivoTranslate }}
              </pui-flex>
            </li>
          </ul>
          <h2
            v-show="!signageRequests || (signageRequests.length === 0 && !isDemandsLoading)"
            class="demands-nodata"
          >
            <span class="mdi mdi-table" />{{ $t('poster.demands.gallery.no_data') }}
          </h2>
        </div>
      </div>
      <div class="beaSidePanel">
        <!-- Filter panel -->
        <pui-slide-panel
          ref="filtersPanel"
          :title="$t('poster.demands.filters.title')"
          :isOpenByDefault="true"
          :showCloseButton="false"
        >
          <template #default>
            <div class="filter-content scroll-body">
              <pui-attribute-group-panel
                v-for="group in filterAttributeGroups"
                :key="group.itemId"
                :group="group"
                class="padding"
              >
                <template v-if="group.filters">
                  <pui-attribute-filter-panel
                    :ref="`formFilter_${filter.alias}`"
                    v-for="filter in group.filters"
                    :key="filter.itemId"
                    :filter="filter"
                    :value="
                      currentFilters[filter.alias] != null
                        ? currentFilters[filter.alias].value
                        : null
                    "
                    :scopeFilter="PosterScopes.Demands"
                    :loadOptionsFunction="getFilterPossibleOptions"
                    @valueInput="filterValueInput"
                    @valueChanged="filterValueChanged"
                  />
                </template>
              </pui-attribute-group-panel>
            </div>
          </template>

          <template #titleRight>
            <pui-flex class="form-action" justifyContent="end" alignItems="center">
              <pui-button-list class="button-list-center">
                <pui-button
                  v-permission="Permissions.LOGS_RESET_FILTERS"
                  v-track:click="Tracking.LOGS_RESET_FILTERS"
                  :disabled="isFilteringDisabled() || isDemandsLoading"
                  :outline="true"
                  @click="clearFilters()"
                  variant="primary"
                >
                  {{ $t('poster.demands.filters.reset_filters') }}
                </pui-button>
                <pui-button
                  v-permission="Permissions.LOGS_FILTER"
                  :outline="false"
                  :disabled="isDemandsLoading"
                  @click="filter()"
                  variant="primary"
                >
                  {{ $t('poster.demands.filters.apply_filters') }}
                </pui-button>
              </pui-button-list>
            </pui-flex>
          </template>
        </pui-slide-panel>

        <!-- Selection slide panel -->
        <pui-panel>
          <div v-if="selectedSignages.length == 1" class="informations-content scroll-body">
            <pui-common-spinner v-show="isSignageDetailsLoading" position="fixed" />
            <pui-button-list class="top-buttongroup-action padding">
              <pui-button
                key="btn-edit"
                v-permission="Permissions.LOGS_EDIT"
                v-track:click="Tracking.LOGS_EDIT_SIGNAGE"
                :outline="false"
                :disabled="noSelectedElements()"
                @click="openEditDialog(selectedSignages[0])"
                variant="primary"
              >
                {{ $t('poster.demands.selected_panel.open') }}
              </pui-button>
              <pui-button
                key="btn-download"
                v-permission="Permissions.DOWNLOAD_PDF"
                :outline="false"
                :disabled="noSelectedElements()"
                @click="sendPostersHook(DemandsActions.DOWNLOAD)"
                variant="primary"
              >
                {{ $t('poster.demands.selected_panel.download') }}
              </pui-button>
              <pui-menu-button :buttonProps="{ outline: false, variant: 'primary', flat: false }">
                <pui-flex direction="column" class="top-buttongroup-action-menu-container">
                  <pui-button
                    key="btn-create-from"
                    v-permission="Permissions.CREATE_FROM"
                    :outline="false"
                    :disabled="
                      noSelectedElements() ||
                        selectedSignages.some(p => p.mimeType !== PosterMimeTypes.Poster)
                    "
                    @click="createSignageFrom"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.create_from') }}
                  </pui-button>
                  <pui-button
                    key="btn-print-auto"
                    v-permission="Permissions.PRINT_AUTO"
                    :outline="false"
                    :disabled="noSelectedElements()"
                    @click="sendPostersHook(DemandsActions.PRINT_AUTO)"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.print_auto') }}
                  </pui-button>
                  <pui-button
                    key="btn-archive"
                    v-permission="Permissions.LOGS_ARCHIVE"
                    :outline="false"
                    :disabled="noSelectedElements()"
                    @click="archiveSignages()"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.archive') }}
                  </pui-button>
                  <pui-button
                    key="btn-trash-can"
                    v-permission="Permissions.LOGS_DELETE"
                    :outline="false"
                    :disabled="noSelectedElements()"
                    @click="deleteSignages()"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.delete') }}
                  </pui-button>
                </pui-flex>
              </pui-menu-button>
            </pui-button-list>
            <div :style="{ height: `${CAROUSEL_HEIGHT}px` }" class="demands-thumbnail-wrapper">
              <poster-gallery-thumbnail
                :posterId="selectedSignages[0].itemId"
                class="single-thumbnail"
              />
            </div>
            <pui-group-panel
              v-if="detailsDefinition != null && currentSignageDetails != null"
              :title="$t('poster.demands.selected_panel.info.title')"
              class="padding"
            >
              <pui-attribute-detail-panel
                v-for="detail in detailsDefinition"
                :key="detail.alias"
                :value="currentSignageDetails[detail.alias]"
                :detail="detail"
                :getRenderer="getRenderer"
              />
            </pui-group-panel>
          </div>
          <div
            v-if="selectedSignages.length > 1"
            class="informations-content-multiselection scroll-body"
          >
            <pui-button-list class="top-buttongroup-action padding">
              <pui-button
                key="btn-group-download"
                v-permission="Permissions.DOWNLOAD_PDF"
                :outline="false"
                :disabled="noSelectedElements()"
                @click="sendPostersHook(DemandsActions.DOWNLOAD)"
                variant="primary"
              >
                {{ $t('poster.demands.selected_panel.download') }}
              </pui-button>
              <pui-menu-button :buttonProps="{ outline: false, variant: 'primary', flat: false }">
                <pui-flex direction="column" class="top-buttongroup-action-menu-container">
                  <pui-button
                    key="btn-group-create-from"
                    v-permission="Permissions.CREATE_FROM"
                    :outline="false"
                    :disabled="
                      noSelectedElements() ||
                        selectedSignages.some(p => p.mimeType !== PosterMimeTypes.Poster)
                    "
                    @click="createSignageFrom"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.create_from') }}
                  </pui-button>
                  <pui-button
                    key="btn-group-print-auto"
                    v-permission="Permissions.PRINT_AUTO"
                    :outline="false"
                    :disabled="noSelectedElements()"
                    @click="sendPostersHook(DemandsActions.PRINT_AUTO)"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.print_auto') }}
                  </pui-button>
                  <pui-button
                    key="btn-group-archive"
                    v-permission="Permissions.LOGS_ARCHIVE"
                    :outline="false"
                    :disabled="noSelectedElements()"
                    @click="archiveSignages()"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.archive') }}
                  </pui-button>
                  <pui-button
                    key="btn-group-trash-can"
                    v-permission="Permissions.LOGS_DELETE"
                    :outline="false"
                    :disabled="noSelectedElements()"
                    @click="deleteSignages()"
                    variant="secondary"
                    flat
                    block
                  >
                    {{ $t('poster.demands.selected_panel.delete') }}
                  </pui-button>
                </pui-flex>
              </pui-menu-button>
            </pui-button-list>
            <div :style="{ height: `${CAROUSEL_HEIGHT}px` }" class="demands-thumbnail-wrapper">
              <pui-carousel
                ref="carousel"
                :items="carouselMultiplePosters"
                :options="carouselOptions"
                :getItemId="getPosterId"
                class="demands-thumbnail__carousel"
              >
                <template #default="{item}">
                  <poster-gallery-thumbnail
                    :posterId="item.itemId"
                    @loaded="onCarouselThumbnailLoaded"
                    class="carousel-thumbnail"
                  />
                </template>
              </pui-carousel>
            </div>
            <p class="select-count">
              {{ nbSelectedElementsDescription }}
            </p>
          </div>
        </pui-panel>
      </div>
    </div>

    <!-- Signage edition -->
    <pui-edit-poster-dialog
      ref="editPosterDialog"
      v-if="signageRequests && signageRequests.length"
      @closed="onEditClosed"
    ></pui-edit-poster-dialog>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';

import helpers from '../../../core/helpers';
import router from '../../../core/router';
import services from '../../../core/services';
import store from '../../../core/store';
import { Loader } from '../../../utils/loader';
import puiEditPosterDialog from '../components/demands/EditPosterDialog';
import puiSendPosterSummaryDialog from '../components/SendPosterSummaryDialog.vue';
import { PosterTableEditors } from '../components/table/editors';
import { PosterTableRenderers } from '../components/table/renderers';
import {
  AttributeAlias,
  DemandsActions,
  FilterAlias,
  PosterMimeTypes,
  PosterScopes,
  Statutes,
} from '../constants';
import { POSTER_USER_SETTINGS, Settings } from '../constants/userSettings';
import { POSTER_SESSION_SETTINGS, SessionSettings } from '../constants/sessionSettings';
import { getPosterLimit, getPosterStatusColor } from '../helpers/posterHelper';
import ConstantsMixin from '../mixins/ConstantsMixin';
import { getPosterService } from '../services';
import {
  NAMESPACE as POSTER_NAMESPACE,
  RESET_SIGNAGES_PAGINATION,
  SET_CURRENT_SIGNAGE_ITEM_ID,
  SET_SELECTED_SIGNAGES,
} from '../store/modules/poster';
import { filterUpdateOn } from '../helpers/filterHelpers';
import { AttributeTypes } from '../../common/constants';

const ViewModes = {
  LIST: 'LIST',
  GALLERY: 'GALLERY',
};

const CAROUSEL_HEIGHT = 300;

export default {
  components: {
    puiEditPosterDialog,
    puiSendPosterSummaryDialog,
  },
  mixins: [ConstantsMixin],
  watch: {
    selectedSignages: {
      immediate: true, // Update on mount, in case there is already a selection
      /**
       * Watches the selected signages to update the carousel multiple posters
       *
       * @param {object[]} nextValue - the new selection of posters
       * @returns {void}
       */
      handler: async function selectedSignages(nextValue) {
        // This only applies to the carousel when showing multiple posters
        if (nextValue.length > 1) {
          this.carouselMultiplePosters = [...nextValue].reverse();

          if (
            this.$refs.carousel &&
            this.$refs.carousel.getLength() < this.carouselMultiplePosters.length
          ) {
            // Wait for carousel to render new items
            await this.$nextTick();
            // If we added items to the selection, go to the last added poster
            // which is at the first position in our carousel
            this.$refs.carousel.go(0, false);
          }
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      user: 'user',
      currentFilters: `${POSTER_NAMESPACE}/currentFilters`,
    }),
    signageItemsCart: {
      get() {
        return getPosterService('cartState').get();
      },
      set(nextSignageItems) {
        getPosterService('cartState').set(nextSignageItems);
      },
    },
    pagination: {
      get() {
        return store.state[POSTER_NAMESPACE].signagesPagination;
      },
      set(pagination) {
        store.commit(`${POSTER_NAMESPACE}/SET_SIGNAGES_PAGINATION`, pagination);
      },
    },
    currentSorts: {
      get() {
        return (
          services
            .getService('sessionSettings')
            .getInCategory(POSTER_SESSION_SETTINGS, SessionSettings.DEMANDS_SORTS) ||
          this.defaultSorts
        );
      },
      set(sorts) {
        services
          .getService('sessionSettings')
          .setInCategory(POSTER_SESSION_SETTINGS, SessionSettings.DEMANDS_SORTS, sorts);
      },
    },
    selectedSignages: {
      get() {
        return store.state[POSTER_NAMESPACE].selectedSignages;
      },
      set(selection) {
        store.commit(`${POSTER_NAMESPACE}/${SET_SELECTED_SIGNAGES}`, selection);
      },
    },
    /**
       * Number of selected elements description.
       @returns {String} the number of selected elements as string
       */
    nbSelectedElementsDescription() {
      const nbSelected = this.selectedSignages.length;

      return this.$tc('poster.demands.selected_panel.selected_count', nbSelected, {
        count: nbSelected,
      });
    },
    taintedRenderer() {
      return PosterTableRenderers.getRenderer('TaintedRenderer');
    },
    commentRenderer() {
      return PosterTableRenderers.getRenderer('CommentRenderer');
    },
    /**
     * @returns {string} the list view mode
     */
    viewMode() {
      return (
        services
          .getService('userSettings')
          .getInCategory(POSTER_USER_SETTINGS, Settings.DEMANDS_LIST_MODE) || ViewModes.LIST
      );
    },
    /**
     * @returns the max number of posters that can be sent
     */
    posterLimit() {
      return getPosterLimit();
    },
  },
  methods: {
    isoDate(value) {
      return Vue.filter('isoDate')(value);
    },
    abstractDate(value) {
      return Vue.filter('abstractDate')(value);
    },
    abstractNumber(value) {
      return Vue.filter('abstractNumber')(value);
    },
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    piivoTranslateLabel(value) {
      return Vue.filter('piivoTranslateLabel')(value);
    },
    getRenderer(type) {
      return PosterTableRenderers.getRenderer(type);
    },
    /**
     * @param {object} status - the status poster object
     * @returns {string} the color of the status
     */
    getPosterStatusColor(status) {
      return getPosterStatusColor(status);
    },
    /**
     * Switch view mode with parameter.
     * @param {String} mode : New mode
     */
    switchViewMode(mode) {
      services
        .getService('userSettings')
        .setInCategory(POSTER_USER_SETTINGS, Settings.DEMANDS_LIST_MODE, mode);
    },
    /**
     * Reloads the page to recover from a fonts error
     */
    reloadFromFontsError() {
      this.$router.go();
    },
    // CAROUSEL
    getPosterId(poster) {
      return poster.itemId;
    },
    /**
     * Refreshes the carousel when a thumbnail changes.
     * This is because when using autoWidth, the carousel will
     * size according to the first size of the image. But since
     * we load our images asynchronously, their real sizes are not
     * available immediately
     *
     * @returns {void}
     */
    onCarouselThumbnailLoaded() {
      this.$nextTick(() => {
        if (this.$refs.carousel) {
          this.$refs.carousel.refresh();
        }
      });
    },
    // LOADING
    /**
     * Start loading demands with message (show spinner).
     * @param {String} message : Loading message
     */
    startDemandsLoading(message) {
      this.currentDemandsMessage = message;
      this.isDemandsLoading = true;
    },
    /**
     * Stop demands loading.
     */
    stopDemandsLoading() {
      this.isDemandsLoading = false;
    },
    /**
     * Edit the signage in parameter.
     * @param {Object} signage : The signage to edit
     */
    openEditDialog(signage) {
      const signageRequestItemIdx = this.signageRequests.findIndex(
        s => s.itemId === signage.itemId
      );
      if (signageRequestItemIdx !== -1) {
        this.$refs.editPosterDialog.open(this.signageRequests, signageRequestItemIdx);
      }
    },
    /**
     * Cancel edition.
     *
     * @param {object[]} editedPosterRequests - array of poster requests that were modified
     * (these objects don't contain the edited data)
     */
    onEditClosed(editedPosterRequests) {
      if (editedPosterRequests.length) {
        this.startSynchronizationDelay(() => {
          // Refresh page (refresh signages thumbnails and informations)
          this.updatePage();
        });
      }
    },
    /**
     * Start loading signage details (show spinner).
     */
    startSignageDetailsLoading() {
      this.isSignageDetailsLoading = true;
    },
    /**
     * Stop signage details loading.
     */
    stopSignageDetailsLoading() {
      this.isSignageDetailsLoading = false;
    },
    /**
     * Initializes 'during-sync' listener for the poster sync
     */
    initDuringSyncListener() {
      if (this.unsubDuringGlobalSync) {
        this.unsubDuringGlobalSync();
      }

      // Setup default action when syncing: show global loading
      this.unsubDuringGlobalSync = getPosterService('signagesSync').actionDuringSynchronization(
        () => {
          if (this.stopGlobalSyncLoading) {
            // Cancel existing loader so we only have 1
            this.stopGlobalSyncLoading();
          }

          // Initialize a 'default' loader when a sync is active to prevent
          // the user interacting with the page
          this.stopGlobalSyncLoading = this.globalLoader.addMessage(
            this.$t('poster.demands.loading.updating_list')
          );
        }
      );
    },
    /**
     * Initializes 'this.postSync' as the 'after-sync' callback for the poster sync
     */
    initAfterSyncListener() {
      if (this.unsubAfterGlobalSync) {
        this.unsubAfterGlobalSync();
      }
      this.unsubAfterGlobalSync = getPosterService('signagesSync').actionAfterSynchronization(
        this.postSync,
        { once: false, disableImmediate: false }
      );
    },
    /**
     * Removes the poster sync listeners
     */
    removeSyncListeners() {
      if (this.unsubDuringGlobalSync) {
        this.unsubDuringGlobalSync();
      }
      if (this.unsubAfterGlobalSync) {
        this.unsubAfterGlobalSync();
      }
    },
    /**
     * Saves the callback to be invoked once the next delay is over
     * **WARNING**: if no sync is currently active, the callback
     * will only be invoked once a sync starts and finishes
     *
     * @param {Function} cb - invoked after the delay is over
     */
    setAfterSynchronizationCb(cb) {
      // We can safely overwrite the current cb, since there should
      // only be 1 'after-sync' action per sync
      this.afterSynchronizationCb = cb;
    },
    /**
     * Callback to execute after all synchronizations.
     * Stops global loading. Then checks if anyone registered a
     * 'afterSynchronizationCb' callback and invokes it.
     */
    postSync() {
      // Always stop the global sync loading from 'actionDuringSynchronization'
      if (this.stopGlobalSyncLoading) {
        this.stopGlobalSyncLoading();
        this.stopGlobalSyncLoading = null;
      }
      // Invoke the current afterSync callback if it exists
      if (this.afterSynchronizationCb) {
        this.afterSynchronizationCb();
        this.setAfterSynchronizationCb(null);
      } else if (!this.preventDefaultAfterSyncCb) {
        // Otherwise reload the demands list
        this.updatePage();
      }
    },
    /**
     * Saves the callback to be invoked once the next delay is over and
     * starts a new synchronization delay
     *
     * @param {Function} cb - invoked after the delay is over
     */
    startSynchronizationDelay(cb) {
      this.setAfterSynchronizationCb(cb);
      getPosterService('signagesSync').startSynchronizationDelay();
    },
    // PREVIEWS
    /**
     * Generates preview poster objects from 'this.selectedSignages' for the given page range
     * @param {number} pageIndex - index of the page of previews to generate
     * @param {number} pageSize - the number of previews per page
     */
    async generatePreviewData(pageIndex, pageSize) {
      const stopGlobalLoading = this.globalLoader.addMessage(
        this.$t('poster.demands.send_summary_dialog.loading.form')
      );

      let templates = [];

      try {
        const start = pageIndex * pageSize;
        const end = start + pageSize;

        const signageIds = this.postersToSend.slice(start, end).map(signage => signage.itemId);
        templates = await getPosterService('signages').loadSignages(signageIds, true);
      } catch (err) {
        if (err.fontsError) {
          this.fontsError = true;
        } else {
          this.$refs.sendPosterSummaryDialog.closeSilently();
        }

        services
          .getService('alerts')
          .alertError(this.$t('poster.demands.send_summary_dialog.error.posters'));

        templates = [];
      }

      stopGlobalLoading();
      return templates;
    },
    // SEND DIALOG
    /**
     * Retrieves the action's creation form, then either
     * opens the confirmation dialog or directly sends the posters
     *
     * @param {String} currentAction - the type of "send" to execute: create, download...
     */
    async sendPostersHook(currentAction) {
      if (this.selectedSignages.length <= 0) {
        return;
      }

      const confirmedLimits = await new Promise(resolve => {
        // If limit exceeded, confirm limited send
        if (this.posterLimit > 0 && this.selectedSignages.length > this.posterLimit) {
          if (this.$refs.sendLimitedCountConfirmationDialog) {
            this.$refs.sendLimitedCountConfirmationDialog.open({
              sendAction: currentAction,
              onCancel: closeDialog => {
                closeDialog();
                resolve(false);
              },
              onConfirm: closeDialog => {
                closeDialog();
                this.postersToSend = this.selectedSignages.slice(0, this.posterLimit);
                resolve(true);
              },
            });
          } else {
            resolve(false);
          }
        } else {
          // Else send without limit confirmation
          this.postersToSend = [...this.selectedSignages];
          resolve(true);
        }
      });
      if (!confirmedLimits) {
        return;
      }

      const stopGlobalLoading = this.globalLoader.addMessage(
        this.$t('poster.demands.send_summary_dialog.loading.posters')
      );

      let hasActionConfigurationError = false;
      let hasActionFormError = false;
      let currentActionForm = null;

      let actionConf = null;

      try {
        actionConf = await getPosterService('sendPosters').retrieveActionConfiguration(
          currentAction
        );
      } catch (err) {
        hasActionConfigurationError = true;
        getPosterService('sendPosters').clearActionConfiguration(currentAction);
      }

      if (actionConf && actionConf.form) {
        try {
          currentActionForm = await getPosterService('sendPosters').retrieveActionForm(
            currentAction
          );
        } catch (err) {
          hasActionFormError = true;
          getPosterService('sendPosters').clearActionForm(currentAction);
        }
      }

      stopGlobalLoading();

      if (hasActionConfigurationError) {
        services
          .getService('alerts')
          .alertError(this.$t('poster.demands.send_summary_dialog.error.form'));
        // Since we don't know if there is a form, we don't know if we should
        // display the confirmation dialog: just alert and stop
        return;
      }

      this.currentAction = currentAction;
      const defaultShowAllPrintables = false;

      if (currentActionForm) {
        this.$refs.sendPosterSummaryDialog.open({
          form: currentActionForm,
          showFormError: hasActionFormError,
          showGenericError: false,
          defaultShowAllPrintables,
          totalPosterCount: this.postersToSend.length,
        });
      } else {
        await this.onSendConfirmation({
          values: [
            {
              attributeAlias: AttributeAlias.SHOW_ALL_PRINTABLES,
              value: defaultShowAllPrintables,
            },
          ],
        });
      }
    },
    /**
     * Called when the send poster dialog is closed by itself
     */
    onSendPosterDialogClosed() {
      this.currentAction = null;
    },
    /**
     * Handles the send poster dialog confirmation
     *
     * @param {object} opts - send options
     * @param {object} otps.parameters - parameters to send to creation
     */
    async onSendPosterDialogConfirmation({ parameters }) {
      await this.onSendConfirmation(parameters);

      this.$refs.sendPosterSummaryDialog.closeSilently();
    },
    /**
     * Handles the send confirmation via `this.currentAction`
     *
     * @param {object} parameters - parameters to send to download/print
     */
    async onSendConfirmation(parameters) {
      switch (this.currentAction) {
        case DemandsActions.DOWNLOAD:
          await this.downloadPDF(parameters);
          break;
        case DemandsActions.PRINT_AUTO:
          await this.printAutoConfirmation(parameters);
          break;
      }
    },
    /**
     * Download or print all pdf documents and update signages statutes to printed status.
     *
     * @param {object} parameters - download parameters
     */
    async downloadPDF(parameters) {
      const stopGlobalLoading = this.globalLoader.addMessage(
        this.$t('poster.demands.loading.processing_print_or_download')
      );

      await new Promise((resolve, reject) => {
        const selectedSignageIds = this.postersToSend.map(s => s.itemId);
        (() => {
          getPosterService('pdfConverter')
            .convertToPdf(selectedSignageIds, parameters, this.Tracking.LOGS_DOWNLOAD_SIGNAGE, {
              resolve,
              reject,
            })
            .finally(() => {
              // Stop loading and close the send dialog as soon as the conversion is started
              stopGlobalLoading();
              this.$refs.sendPosterSummaryDialog.closeSilently();
            });
        })();
      });

      this.$refs.demandsTable.resetSelection();
      this.selectedSignages = [];
      this.postersToSend = [];

      this.startSynchronizationDelay(() => {
        this.updatePage();
      });
    },
    /**
     * Launch automatic print for selected signages (update to status 'WAITING_TO_BE_PRINTED').
     *
     * @param {object} parameters - download parameters
     */
    async printAutoConfirmation(parameters) {
      const stopGlobalLoading = this.globalLoader.addMessage(
        this.$t('poster.demands.loading.processing_print_auto')
      );

      // Update signages statutes
      const selectedSignagesIds = this.postersToSend.map(s => s.itemId);
      try {
        await getPosterService('signages').changeStatuses(
          selectedSignagesIds,
          Statutes.WAITING_TO_BE_PRINTED,
          parameters
        );

        this.startSynchronizationDelay(() => {
          this.updatePage();
        });

        // Clear selection
        this.$refs.demandsTable.resetSelection();
        this.selectedSignages = [];
        this.postersToSend = [];
        services
          .getService('alerts')
          .alertSuccess(this.$t('poster.demands.confirmation.success.print_auto'));
        stopGlobalLoading();

        // Refresh page
        this.updatePage();
      } catch (err) {
        stopGlobalLoading();
      }
    },
    // CREATE FROM
    /**
     * Initializes signage objects for the selected signages, sets them in the cart
     * and redirects to creation page
     *
     * @returns {Promise<void>}
     */
    async createSignageFrom() {
      const selectedIds = this.selectedSignages.map(signage => signage.itemId);
      if (!selectedIds.length) {
        return;
      }

      const stopGlobalLoading = this.globalLoader.addMessage(
        this.$tc('poster.demands.loading.loading_signages_create_from', selectedIds.length)
      );

      try {
        const familiesAndTypes = await getPosterService('templates').loadFamiliesAndTypes();

        const typeToFormatsGroupsMap = new Map();
        const typeToTypeDataMap = new Map();

        /**
         * @param {string} typeId - id of the type to get the formats groups for
         * @returns {Promise<object[]>} the formats groups
         */
        const getFormatsGroupsForType = async typeId => {
          if (typeToFormatsGroupsMap.has(typeId)) {
            return typeToFormatsGroupsMap.get(typeId);
          }

          const formatsGroups = await getPosterService('templates').loadTypeFormatGroups(typeId);
          typeToFormatsGroupsMap.set(typeId, formatsGroups);

          return formatsGroups;
        };

        /**
         * @param {object} type - the type to get the type data for
         * @returns {Promise<object>} the type data
         */
        const getTypeDataForType = async type => {
          if (typeToTypeDataMap.has(type.itemId)) {
            return typeToTypeDataMap.get(type.itemId);
          }

          const typeData = await getPosterService('templates').loadTypeData(type, null);
          typeToTypeDataMap.set(type.itemId, typeData);

          return typeData;
        };

        const templates = await getPosterService('signages').loadSignages(selectedIds, true);
        if (templates.length !== selectedIds.length) {
          throw new Error('Did not receive same amount of templates as signages');
        }

        const newSignages = [];

        for (const template of templates) {
          const posterFamilyTypeEntry = [
            ...familiesAndTypes.rootTypes.entries(),
          ].find(([familyId, rootTypes]) =>
            rootTypes.find(
              rootType =>
                rootType.itemId === template.type.itemId ||
                (rootType.children &&
                  rootType.children.find(subType => subType.itemId === template.type.itemId))
            )
          );

          if (!posterFamilyTypeEntry) {
            throw new Error('Could not find family from signage type');
          }

          const familyId = posterFamilyTypeEntry[0];

          const formatsCopiesMap = {};

          // We only need to init the formats for the current template type
          const formatsGroups = await getFormatsGroupsForType(template.type.itemId);
          formatsGroups.forEach(group => {
            group.formats.forEach(format => {
              formatsCopiesMap[format.itemId] = {
                copies: template.format.itemId === format.itemId ? 1 : 0,
                label: format.label,
              };
            });
          });

          // Create basic signage item
          const signage = getPosterService('signages').buildnewSignageItem(
            getPosterService('signages').getNewSignageId(),
            familyId,
            this.user.extendedProperties
          );

          // Overwrite with our existing values
          signage.values = {
            ...signage.values,
            ...template.bindings,
          };
          signage.type = template.type;
          signage.formatId = template.format.itemId;
          signage.formatsCopiesMap = {
            ...signage.formatsCopiesMap,
            ...formatsCopiesMap,
          };

          const typeData = await getTypeDataForType(template.type);
          signage.inputsSummary = getPosterService(
            'signages'
          ).getInputsSummaryFromValues(signage.values, { form: typeData });

          newSignages.push(signage);
        }

        // Add new items to start of cart
        const nextCartState = [...newSignages, ...this.signageItemsCart];

        services.getNamespacedService(POSTER_SERVICES_NAMESPACE, 'cartState').set(nextCartState);
        store.commit(`${POSTER_NAMESPACE}/${SET_CURRENT_SIGNAGE_ITEM_ID}`, newSignages[0].id);

        router.push({ name: 'posterCreation' });
      } catch (err) {
        // No need for special handling on fonts error since we won't
        // even navigate to creation

        services
          .getService('alerts')
          .alertError(
            this.$tc('poster.demands.error.load_signages_create_from', selectedIds.length)
          );
      }
      stopGlobalLoading();
    },
    // DELETION
    /**
     * Delete a signage.
     * @param {Object} signage : The signage to delete
     */
    deleteSignage(signage) {
      this.selectedSignagesRequests = [signage];
      this.$refs.confirmDeletionDialog.open();
    },
    /**
     * Delete selected signages.
     */
    deleteSignages() {
      this.selectedSignagesRequests = [...this.selectedSignages];
      this.$refs.confirmDeletionDialog.open();
    },
    /**
     * Confirm signage deletion
     */
    async confirmDeletion() {
      this.startDemandsLoading(this.$t('poster.demands.loading.deleting'));
      this.$refs.confirmDeletionDialog.close();

      try {
        await getPosterService('signages').deleteSignages(this.selectedSignagesRequests);

        services.getService('analytics').trackEvent(this.Tracking.LOGS_DELETE_SIGNAGE, null, {
          signagesNumber: this.selectedSignagesRequests.length,
        });

        this.startSynchronizationDelay(() => {
          this.updatePage().then(() => {
            this.$refs.demandsTable.resetSelection();
            this.selectedSignages = [];
          });
        });
      } catch (err) {
        this.stopDemandsLoading();
      }
    },
    /**
     * Cancel signage deletion.
     */
    cancelDeletion() {
      this.$refs.confirmDeletionDialog.close();
    },

    // ARCHIVING
    /**
     * Archive a signage.
     * @param {Object} signage : The signage to archive
     */
    archiveSignage(signage) {
      this.selectedSignagesRequests = [signage];
      this.$refs.confirmArchivingDialog.open();
    },
    /**
     * Delete selected signages.
     */
    archiveSignages() {
      this.selectedSignagesRequests = [...this.selectedSignages];
      this.$refs.confirmArchivingDialog.open();
    },
    /**
     * Confirm signage(s) archiving
     */
    async confirmArchiving() {
      this.startDemandsLoading(this.$t('poster.demands.loading.archiving'));
      this.$refs.confirmArchivingDialog.close();

      try {
        await getPosterService('signages').archiveSignages(this.selectedSignagesRequests);

        services.getService('analytics').trackEvent(this.Tracking.LOGS_ARCHIVE_SIGNAGE, null, {
          signagesNumber: this.selectedSignagesRequests.length,
        });

        this.startSynchronizationDelay(() => {
          this.updatePage().then(() => {
            this.$refs.demandsTable.resetSelection();
            this.selectedSignages = [];
          });
        });
      } catch (err) {
        this.stopDemandsLoading();
      }
    },
    /**
     * Cancel signage archiving.
     */
    cancelArchiving() {
      this.$refs.confirmArchivingDialog.close();
    },

    // SELECTION
    /**
     * Launch on table selection changed event.
     * @param {Object} items : Selected items
     */
    onSelectionchanged(items) {
      if (this.keepFilters === false) {
        this.closeFiltersPanel();
      }

      this.selectedSignages = items;
      if (items.length > 0) {
        this.currentSignage = items[items.length - 1];

        // Get current signage details (only if one signage selected)
        if (
          this.currentSignage != null &&
          this.$refs.demandsTable &&
          this.selectedSignages.length === 1
        ) {
          this.loadSignageDetails();
        }
      }
    },
    /**
     * Toogle all signages (select or unselect all table items).
     */
    toggleSelectAllDemands() {
      if (this.noSelectedElements()) {
        this.$refs.demandsTable.selectAllItems();
      } else {
        this.$refs.demandsTable.resetSelection();
      }
    },
    /**
     * Check if there is no signage selected.
     * @returns True if no signage selected otherwise false.
     */
    noSelectedElements() {
      return this.selectedSignages.length === 0;
    },

    onGalleryItemClick(signageRequest) {
      this.$refs.demandsTable.selectSingleItem(signageRequest);
    },

    // PREVIEW
    /**
     * Toggle filters slide panel.
     */
    toggleFiltersPanel() {
      this.$refs.filtersPanel.toggle();
    },
    /**
     * Close filters slide panel.
     */
    closeFiltersPanel() {
      this.$refs.filtersPanel.close();
    },
    /**
     * Initialize filters (Set default values and refresh filters Objects)
     */
    initFilters() {
      // Set user default store filter
      if (
        this.currentFilters[FilterAlias.STORE] == null ||
        !this.currentFilters[FilterAlias.STORE].value ||
        this.currentFilters[FilterAlias.STORE].value.length === 0
      ) {
        store.commit(`${POSTER_NAMESPACE}/SET_FILTER`, {
          name: FilterAlias.STORE,
          value: this.user.extendedProperties[AttributeAlias.STORE],
          operator: 'eq',
        });
      }

      // Init filters
      for (const group of this.filterAttributeGroups) {
        for (const filter of group.filters) {
          // Set default filter value
          if (filter.options && filter.options.defaultValue != null) {
            store.commit(`${POSTER_NAMESPACE}/SET_FILTER`, {
              name: filter.alias,
              value: filter.options.defaultValue,
              operator: helpers.getHelper('filtersHelper').getFilterOperator(filter),
            });
          }

          // Refresh filters with object from links array
          if (
            filter.attribute != null &&
            filter.attribute.parameters != null &&
            filter.attribute.parameters.links != null
          ) {
            const oldFilter = this.currentFilters[filter.alias];
            if (oldFilter != null) {
              const newFilter = [];
              let searchAliasOrIds = [];
              if (Array.isArray(oldFilter.value)) {
                // Array of object with alias or string alias
                for (const item of oldFilter.value) {
                  searchAliasOrIds.push(typeof item === 'string' ? item : item.itemId);
                }
              } else if (typeof oldFilter.value === 'string') {
                searchAliasOrIds = [oldFilter.value];
              }

              // Search aliases and refresh filter (search in parent and children links)
              for (const searchAliasOrId of searchAliasOrIds) {
                let linkItem = null;
                for (const parent of filter.attribute.parameters.links) {
                  if (
                    parent.itemId.toUpperCase() === searchAliasOrId.toUpperCase() ||
                    parent.alias === searchAliasOrId
                  ) {
                    linkItem = parent;
                    break;
                  } else if (
                    filter.options != null &&
                    filter.options.allowGrouping &&
                    parent.children
                  ) {
                    for (const child of parent.children) {
                      if (
                        child.itemId.toUpperCase() === searchAliasOrId.toUpperCase() ||
                        parent.alias === searchAliasOrId
                      ) {
                        linkItem = child;
                        break;
                      }
                    }
                  }
                }

                if (linkItem != null) {
                  newFilter.push(linkItem);
                }
              }

              store.commit(`${POSTER_NAMESPACE}/SET_FILTER`, {
                name: filter.alias,
                value: newFilter,
                operator: oldFilter.operator,
              });
            }
          }
        }
      }
    },
    /**
     * Is filtering disabled.
     * @returns True if a filter is active, false otherwise
     */
    isFilteringDisabled() {
      for (const filterKey in this.currentFilters) {
        if (this.currentFilters.hasOwnProperty(filterKey)) {
          const filterValue = this.currentFilters[filterKey].value;
          if (
            (Array.isArray(filterValue) && filterValue.length > 0) ||
            (Array.isArray(filterValue) === false && filterValue)
          ) {
            return false;
          }
        }
      }
      return true;
    },
    /**
     * Get the list of applied filters keys.
     * @returns {Array} An array containing the key of filters applied
     */
    getActiveFilterKeys() {
      const activeFilterKeys = [];
      if (this.currentFilters) {
        for (const filterKey in this.currentFilters) {
          if (this.currentFilters.hasOwnProperty(filterKey)) {
            // Count visible filters with value
            const filterValue = this.currentFilters[filterKey].value;
            const isVisible =
              this.filtersVisibilityMap.has(filterKey) && this.filtersVisibilityMap.get(filterKey);
            if (
              isVisible &&
              ((Array.isArray(filterValue) && filterValue.length > 0) ||
                (Array.isArray(filterValue) === false && filterValue) ||
                typeof filterValue === 'boolean')
            ) {
              activeFilterKeys.push(filterKey);
            }
          }
        }
      }

      return activeFilterKeys;
    },
    /**
     * Update number of filters applied.
     */
    updateNbFilters() {
      this.nbAppliedFilters = this.getActiveFilterKeys().length;
    },
    /**
     * Filter 'valueInput' event
     *
     * @param {object} filter - the filter that changed
     * @param {Object} value - New value to set in filter
     * @param {object} operator - filter operator to apply
     */
    filterValueInput(filter, value, operator) {
      store.commit(`${POSTER_NAMESPACE}/SET_FILTER`, { name: filter.alias, value, operator });
    },
    /**
     * Filter 'valueChanged' event
     *
     * @param {object} filter - the filter that changed
     * @param {Object} value - New value to set in filter
     * @param {object} operator - filter operator to apply
     */
    filterValueChanged(filter, value, operator) {
      this.filterValueInput(filter, value, operator);
      this.startUpdateOn(filter);
    },
    /**
     * Clear filters (types, formats etc.)
     */
    clearFilters() {
      store.commit(`${POSTER_NAMESPACE}/CLEAR_FILTERS`);
      this.initFilters();
      this.filter();
    },
    /**
     * @param {object} filter - the filter whose options to retrieve
     * @param {object} parameters - search parameters
     * @param {Number} parameters.context - additional context to load the values
     * @returns {Promise<object[]>} options for the filter
     */
    getFilterPossibleOptions(filter, { context }) {
      return getPosterService('values').getPossibleValues(
        filter.attribute,
        context,
        null,
        bodyParameters =>
          getPosterService('values').addFilterValuesBodyParameters(
            bodyParameters,
            this.currentFilters,
            this.filterAttributeGroups
          )
      );
    },
    /**
     * Launches the "updateOn" mechanism for the filter
     *
     * @param {object} filter - the filter whose updateOn to trigger
     */
    startUpdateOn(filter) {
      const actionCb = cbFilter => {
        if (cbFilter.attribute && cbFilter.attribute.type === AttributeTypes.LINKS) {
          this.$nextTick(() => {
            const attributeFilterPanel = this.$refs[`formFilter_${cbFilter.alias}`];
            if (attributeFilterPanel != null && attributeFilterPanel[0] != null) {
              attributeFilterPanel[0].loadOptions().catch(() => null);
            }
          });
        }
      };

      filterUpdateOn(this.filterAttributeGroups, filter, actionCb);
    },
    // PAGE UPDATE
    /**
     * Filter demands.
     */
    filter() {
      // Reset paging
      store.commit(`${POSTER_NAMESPACE}/${RESET_SIGNAGES_PAGINATION}`);

      // Calculate number of filters applied
      this.updateNbFilters();

      // Update page
      services.getService('analytics').trackEvent(this.Tracking.LOGS_APPLY_FILTERS, {
        filters: this.getActiveFilterKeys().join(','),
      });
      this.updatePage();
    },
    /**
     * Get signages list for current filters, sort and pagination parameters.
     * @param {Boolean} [didResetPageIndex=false] - if we already automatically decremented the page.
     * Will prevent from decrementing a second time
     * @returns {void}
     */
    async updatePage(didResetPageIndex = false) {
      this.startDemandsLoading(this.$t('poster.demands.loading.updating_list'));

      try {
        const results = await getPosterService('signages').listSignages({
          index: this.pagination.pageIndex,
          size: this.pagination.pageSize,
          sortBy: this.currentSorts,
          filterBy: this.currentFilters,
        });

        this.signageRequests = results.body.items;
        this.pagination.nbElements = this.signageRequests.length;
        this.pagination.totalNumberOfItems = results.body.totalNumberOfItems;
        this.pagination.totalNumberOfPages = results.body.totalNumberOfPages;
        this.$forceUpdate();

        // Whenever we update the page, any item or the whole array may
        // be different, so we clear details
        this.clearUnusedSignageDetails();

        // If no more elements on this page, and we are not on first page, decrement the page index
        if (
          !didResetPageIndex &&
          this.pagination.pageIndex > 1 &&
          this.pagination.nbElements === 0
        ) {
          // Update store via computed setter
          this.pagination.pageIndex = Math.max(this.pagination.pageIndex - 1, 1);
          return Promise.resolve(this.updatePage(true));
        }

        if (this.$refs && this.$refs.demandsTable) {
          // Scroll to top on page change
          this.$refs.demandsTable.scrollToTop();

          // Refresh selection with new Listing objects (for selection issues)
          const newSelection = [];
          for (const selectedSignage of this.selectedSignages) {
            const newSignage = this.signageRequests.find(
              signage => signage.itemId === selectedSignage.itemId
            );
            if (newSignage != null) {
              newSelection.push(newSignage);
            }
          }

          // Clear and set new selection (with new instances of objects)
          this.keepFilters = true;
          this.$refs.demandsTable.resetSelection();
          this.$refs.demandsTable.selectItems(newSelection);
          this.keepFilters = false;
        }
      } catch (err) {
        services
          .getService('alerts')
          .alertError(this.$t('poster.demands.error.load_signages_list'));
      }

      this.stopDemandsLoading();
    },
    onPageIndexChanged(index) {
      this.pagination.pageIndex = index;
      this.updatePage();
    },
    /**
     * Column sort event.
     * @param {[sortKey: string, sortDirection: string][]} sorts - sorts entries
     */
    onColumnSort(sorts) {
      this.currentSorts = sorts;

      // Remove any existing 'after-sync' callbacks, so that
      // only ours will be executed, otherwise there might
      // be multiple calls to 'this.updatePage()
      this.preventDefaultAfterSyncCb = true;
      this.setAfterSynchronizationCb(null);

      // Use the signagesSync service to queue a 'after-sync' update
      // instead of using 'this.startSynchronizationDelay' because
      // we don't want to start a **new** delay.
      getPosterService('signagesSync').actionAfterSynchronization(
        () => {
          // Do this async
          this.updatePage();
          // Restore default 'after-sync' behavior
          this.preventDefaultAfterSyncCb = false;
        },
        { once: true, disableImmediate: false }
      );
    },
    /**
     * Retrieves the details for `this.currentSignage` if not already loaded
     * @returns {void}
     */
    async loadSignageDetails() {
      if (!this.currentSignage) {
        return;
      }

      // Only fetch details if we did not already retrieve it
      if (this.didRequestSignageDetails[this.currentSignage.itemId]) {
        this.currentSignageDetails = this.requestedSignageDetails[this.currentSignage.itemId];
        return;
      }

      // Set flag to keep track of requests
      this.$set(this.didRequestSignageDetails, this.currentSignage.itemId, true);

      this.startSignageDetailsLoading();

      try {
        const signageDetails = await getPosterService('signages').getSignageDetails(
          this.currentSignage.itemId
        );
        this.stopSignageDetailsLoading();
        this.currentSignageDetails = signageDetails;
        this.$set(this.requestedSignageDetails, this.currentSignage.itemId, signageDetails);
      } catch (err) {
        this.stopSignageDetailsLoading();
        this.currentSignageDetails = null;

        // Remove flag so that we can retry this request if called again
        this.$set(this.didRequestSignageDetails, this.currentSignage.itemId, false);
      }
    },
    /**
     * Clears the details for signages that are not in `this.signageRequests`
     * anymore
     * @returns {void}
     */
    clearUnusedSignageDetails() {
      Object.keys(this.requestedSignageDetails).forEach(requestedItemId => {
        if (!this.signageRequests.find(signage => signage.itemId === requestedItemId)) {
          this.didRequestSignageDetails[requestedItemId] = false;
          this.requestedSignageDetails[requestedItemId] = null;
        }
      });
    },
  },
  /**
   * Mounted hook.
   */
  mounted() {
    // Setup the 'during-sync' listener right away (so we have a dedicated loading
    // state asap)
    // DO NOT setup the 'after-sync' listener right away, otherwise it might
    // trigger a list update before we loaded the prerequisites (filters)
    this.initDuringSyncListener();

    const stopGlobalLoading = this.globalLoader.addMessage();

    // Get filters configuration. Do this async
    // eslint-disable-next-line promise/catch-or-return
    getPosterService('signages')
      .getLogsFiltersDefinition()
      // eslint-disable-next-line promise/prefer-await-to-then
      .then(async logsFiltersDefinition => {
        // Compute and set filters definition
        helpers.getHelper('filtersHelper').computeFilters(logsFiltersDefinition.filtersGroups);
        this.filterAttributeGroups = logsFiltersDefinition.filtersGroups;

        // Initialize filters
        this.initFilters();

        // Init filters visibility map
        for (const group of this.filterAttributeGroups) {
          for (const filter of group.filters) {
            const isVisible = !filter.options || filter.options.visible !== false;
            this.filtersVisibilityMap.set(filter.alias, isVisible);
          }
        }

        // Update number of filters applied
        this.updateNbFilters();

        // eslint-disable-next-line promise/always-return
        try {
          // Get table columns configuration
          const logsColumnsDefinition = await getPosterService('signages').getLogsTableDefinition();
          if (
            logsColumnsDefinition.paging &&
            logsColumnsDefinition.paging.pageSizes &&
            logsColumnsDefinition.paging.pageSizes.length
          ) {
            // Mutate the 'pagination' object to trigger the computed setter
            this.pagination = {
              ...this.pagination,
              pageSize: logsColumnsDefinition.paging.pageSizes[0],
            };
          }

          // Compute table columns
          helpers.getHelper('tableHelper').computeTableColumns(logsColumnsDefinition.columns);
          this.columns = logsColumnsDefinition.columns;

          // Set default sorts
          const defaultSorts = helpers
            .getHelper('tableHelper')
            .computeTableSorts(logsColumnsDefinition.defaultSort);

          this.defaultSorts = defaultSorts;

          // Once we have our filters and sorts, we can load the table
          // Initializing the 'after-sync' enables the default behavior which is to reload
          // the table: see this.postSync
          this.initAfterSyncListener();
        } catch (err) {
          this.columns = [];
        }
      })
      .catch(() => {
        this.filterAttributeGroups = [];
      })
      .finally(() => {
        stopGlobalLoading();
      });

    // Get details and print details configuration. Do this async
    getPosterService('signages')
      .getLogsDetailsDefinition()
      // eslint-disable-next-line promise/prefer-await-to-then, promise/always-return
      .then(logsDetailsDefinition => {
        helpers.getHelper('detailsHelper').computeDetailsDefinition(logsDetailsDefinition.details);
        this.detailsDefinition = logsDetailsDefinition.details;
        helpers
          .getHelper('detailsHelper')
          .computeDetailsDefinition(logsDetailsDefinition.printDetails);
        this.printDetailsDefinition = logsDetailsDefinition.printDetails;
      })
      .catch(() => {
        this.detailsDefinition = [];
        this.printDetailsDefinition = [];
      });
  },
  /**
   * Before destroyed hook
   */
  beforeDestroy() {
    this.removeSyncListeners();
  },
  data() {
    return {
      // CAROUSEL
      CAROUSEL_HEIGHT,
      carouselOptions: {
        type: 'slide',
        height: `${CAROUSEL_HEIGHT}px`,
        autoWidth: true,
        // Prevent clones from showing at start/end
        trimSpace: false,
      },
      carouselMultiplePosters: [],
      // OTHER
      isDemandsLoading: false, // Demands listing loading

      unsubDuringGlobalSync: null,
      unsubAfterGlobalSync: null,
      afterSynchronizationCb: null,
      stopGlobalSyncLoading: null,
      preventDefaultAfterSyncCb: false,
      globalLoader: new Loader(),

      currentDemandsMessage: '',

      currentSignage: null,
      currentSignageDetails: null,
      isSignageDetailsLoading: false,
      didRequestSignageDetails: {},
      requestedSignageDetails: {},
      columns: [],
      defaultSorts: [],
      selectedSignagesRequests: [],
      selectAll: false,
      signageRequests: [],
      detailsDefinition: [],
      printDetailsDefinition: [],
      ViewModes,

      fontsError: false,

      DemandsActions: DemandsActions,
      currentAction: null,
      postersToSend: [],

      PosterTableEditors,
      PosterTableRenderers,

      filtersVisibilityMap: new Map(),
      nbAppliedFilters: 0,
      filterAttributeGroups: [],
      keepFilters: false,

      PosterMimeTypes,
      PosterScopes,
    };
  },
};
</script>
